/**
 * The share wizard's send machine: everything between "the sender has committed
 * to these messages" and "every one of them is marked sent".
 *
 * One trip through the wizard composes a send plan, then works through it a leg
 * at a time — create the leg, hand off to the SMS app, come back, mark it sent,
 * open the next. That loop is what this hook owns: the sharing run (#455), the
 * leg creation and its contact enrichment, the handoff itself (the return
 * notification, the SMS link, the pre-handoff write), the confirm steps, and
 * the ways out of a send in progress.
 *
 * It knows nothing about composing. Three functions are the whole of what it
 * asks the composer for — `finalizeBody`, `legStoryFor` and `startingLegStoryFor` — so
 * templates, field values, pronouns and the allowed name stay on the other side
 * of that line. That is deliberate: the sending steps are headed for a route of
 * their own, entered once from composition and never returned from, and on that
 * day these three are what has to be answerable from the stored session rather
 * than from live compose state. Nothing else here reaches back.
 */
import { useCallback, useState } from 'react';
import type React from 'react';
import { Contact, ContactList, ContactLogic } from '@/modules/contact';
import type { ContactAction } from '@/modules/contact/reducer';
import type { MessageDraft, SharingLegDetails } from '@/modules/message';
import type { MessageIntent } from '@/modules/template';
import type { Topic } from '@/modules/topic/types';
import { apiCall } from '@/util/api';
import { MePrFoService, type ReshareInfoLike } from '@/services/meprfo';
import {
  createContactEnrichment,
  todayISO,
  type ContactEnrichment,
} from '@/hooks/useSnowballSync';
import type { DeviceInfo } from '@/hooks/useDeviceInfo';
import { Destinations } from './navigation_logic';
import type { ShareNavigator } from './types';

const RETURN_NOTIFICATION_BODY: Record<
  MessageIntent,
  { noMore: string; withMore: string }
> = {
  intro: {
    noMore: "We'll record who you just introduced to the topic",
    withMore:
      "There's more recipients in your queue, plus we'll record who you introduced to the topic",
  },
  reminder: {
    noMore: "We'll record who you just reminded of the topic",
    withMore:
      "There's more recipients in your queue, plus we'll record who you reminded of the topic",
  },
  update: {
    noMore: "We'll record who you updated about the topic",
    withMore:
      "There's more recipients in your queue, plus we'll record who you updated about the topic",
  },
};

export function getReturnNotificationBody(
  intent: MessageIntent,
  hasMoreToShare: boolean
): string {
  return RETURN_NOTIFICATION_BODY[intent][
    hasMoreToShare ? 'withMore' : 'noMore'
  ];
}

export async function scheduleFCMReturnNotification(
  fcmToken: string | null,
  title: string,
  body: string,
  link: string
): Promise<void> {
  if (!fcmToken) return;
  await apiCall('/api/notifications/push', {
    method: 'POST',
    body: JSON.stringify({
      token: fcmToken,
      title,
      body,
      link,
    }),
  });
}

/**
 * What creating a leg learned about its recipients' Snowball accounts, as the
 * address-book updates that record it: a recipient whose cached account id the
 * server replaced, and one the server created an account for.
 */
export function contactEnrichmentsForLeg(
  recipients: readonly Contact[],
  result: {
    replacedUserIds?: Record<string, string>;
    createdUserIds?: Record<string, string>;
  }
): ContactEnrichment[] {
  const today = todayISO();
  const enrichPayload: ContactEnrichment[] = [];

  const replacedUserIds = result.replacedUserIds ?? {};
  const createdUserIds = result.createdUserIds ?? {};

  if (Object.keys(replacedUserIds).length > 0) {
    recipients
      .filter((c: Contact) => c.snowballId && replacedUserIds[c.snowballId])
      .forEach((c: Contact) => {
        enrichPayload.push(
          createContactEnrichment(
            c.localId,
            replacedUserIds[c.snowballId as string],
            c.snowballId,
            today
          )
        );
      });
  }

  if (Object.keys(createdUserIds).length > 0) {
    recipients
      .filter((c: Contact) => !c.snowballId)
      .forEach((c: Contact) => {
        const phone = ContactLogic.toContactLike(c).phone as string;
        const resolved = createdUserIds[phone];
        if (resolved)
          enrichPayload.push(
            createContactEnrichment(c.localId, resolved, c.snowballId, today)
          );
      });
  }

  return enrichPayload;
}

/**
 * What a leg records about the sender who sent it — the columns a reshare
 * story is later rendered from. Named for the backend's `LegStoryInfo`
 * (`internal/message/sharing_run.go`), which is where these end up.
 *
 * Every field is optional, because every one of them is the sender's to
 * withhold: they can decline to write a note, leave their pronouns unset,
 * refuse to let a name travel, or say nothing about how they know the person
 * who shared it with them.
 *
 * The same shape describes the starting leg of a sharing run (#455), whose
 * story is frozen at the moment the flow commits to sending.
 */
export interface LegStoryInfo {
  allowedDetail?: string;
  senderPronouns?: string;
  /** The name the sender agrees to have travel with the story. */
  allowedSenderName?: string;
  /** The sender's first-person connection clause, stored verbatim. */
  connectionToPrevLeg?: string;
}

/**
 * Everything a draft's leg is created with apart from its recipients: the
 * body of `POST /api/sharing-legs` for that draft, as the send loop sends it
 * and as the outbox stores it to send later.
 *
 * `previousLegId` is where the leg hangs: the leg a reshare passes on, and
 * otherwise the starting leg of the sender's own run (#455).
 */
export function sharingLegDetailsFor(
  draft: MessageDraft,
  context: {
    topicId: string;
    story: LegStoryInfo;
    previousLegId: string | undefined;
    updateSummary: string;
    aboutChangesSince: string | undefined;
  }
): SharingLegDetails {
  const { topicId, story, previousLegId, updateSummary, aboutChangesSince } =
    context;
  // The summary/since metadata belongs to update-intent legs only — in the
  // update flow, never-introduced recipients still get intro legs.
  const isUpdateLeg = draft.header.intent === 'update';
  // Whether this leg passes on the one the sender received. The draft says so
  // rather than the intent: a reshare carries whichever intent its recipient
  // needs, so the intent cannot tell the two apart.
  const isReshare = draft.header.type === 'reshare';
  return {
    topicId,
    intent: draft.header.intent,
    type: draft.header.type,
    ...(isUpdateLeg && updateSummary.trim()
      ? { updateSummary: updateSummary.trim() }
      : {}),
    ...(isUpdateLeg && aboutChangesSince ? { aboutChangesSince } : {}),
    ...(isReshare ? { allowedSenderName: story.allowedSenderName } : {}),
    ...(story.allowedDetail ? { allowedDetail: story.allowedDetail } : {}),
    ...(story.senderPronouns ? { senderPronouns: story.senderPronouns } : {}),
    // A send off the flow's own starting leg carries no connection clause —
    // the sender IS the starting leg author, so it collapses into the starting
    // leg in the reshare story a recipient sees (#455). A reshare sends the
    // first-person pick as-is; the backend stores it verbatim and relativizes
    // at read time. A reshare with no connection (a classic topic never asks)
    // leaves it unset, and the story reads "I know".
    ...(previousLegId ? { previousLegId } : {}),
    ...(isReshare && previousLegId && story.connectionToPrevLeg
      ? { connectionToPrevLeg: story.connectionToPrevLeg }
      : {}),
  };
}

export interface ShareControllerDeps {
  topic: Topic | null;

  // ── what the composer answers ─────────────────────────────────────────────
  /**
   * The draft's body as it will actually be texted. Separate from the body the
   * draft already carries because the leg link can only be woven in once the
   * leg exists, which is halfway through the handoff.
   */
  finalizeBody: (draft: MessageDraft, legId: string) => string;
  /** What this leg records about its sender. */
  legStoryFor: (draft: MessageDraft) => LegStoryInfo;
  /** The same, for the starting leg of the run this flow opens (#455). */
  startingLegStoryFor: (intent: MessageIntent) => LegStoryInfo;

  // ── the session (useWizardSession) ────────────────────────────────────────
  sendQueue: MessageDraft[];
  sendQueueCursor: number;
  inProgressLegId: string | null;
  previousLegId: string | undefined;
  aboutChangesSince: string | undefined;
  updateSummary: string;
  setInProgressLegId: (legId: string | null) => void;
  setSendQueue: React.Dispatch<React.SetStateAction<MessageDraft[]>>;
  setSendQueueCursor: (cursor: number) => void;
  setConfirmedContactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSharingRunId: (legId: string | undefined) => void;
  readSharingRunId: () => string | undefined;
  resetSession: () => void;
  clearStoredSession: () => void;
  persistNow: (overrides?: {
    inProgressLegId?: string;
    sendQueueCursor?: number;
  }) => Promise<void>;

  // ── where the flow goes next ──────────────────────────────────────────────
  nav: ShareNavigator;

  // ── the world outside ─────────────────────────────────────────────────────
  recipients: ContactList;
  dispatch: React.Dispatch<ContactAction>;
  deviceInfo: DeviceInfo;
  fcmToken: string | null;
  presentToast: (options: {
    message: string;
    duration?: number;
    color?: string;
  }) => void;
  /**
   * Record a leg the sender has just confirmed: who now knows about the topic,
   * and who is done. Called once per leg, when the sender proceeds.
   */
  onLegSent: (draft: MessageDraft) => void;
}

export function useShareController({
  topic,
  finalizeBody,
  legStoryFor,
  startingLegStoryFor,
  sendQueue,
  sendQueueCursor,
  inProgressLegId,
  previousLegId,
  aboutChangesSince,
  updateSummary,
  setInProgressLegId,
  setSendQueue,
  setSendQueueCursor,
  setConfirmedContactIds,
  setSharingRunId,
  readSharingRunId,
  resetSession,
  clearStoredSession,
  persistNow,
  nav,
  recipients,
  dispatch,
  deviceInfo,
  fcmToken,
  presentToast,
  onLegSent,
}: ShareControllerDeps) {
  // The reshare info the last leg's creation returned — the final-confirm
  // step's goal-crossing celebration reads them (#447).
  const [lastReshareInfo, setLastReshareInfo] =
    useState<ReshareInfoLike | null>(null);
  // How many recipients this flow confirmed, in total. Set once, by the final
  // confirm; an empty send queue beside a non-zero count is what
  // FinalShareConfirmStep renders as the completion screen.
  const [totalConfirmedCount, setTotalConfirmedCount] = useState<number>(0);
  // The sender returned by tapping their own leg link rather than the confirm
  // button, so the confirm has already happened in effect (#295).
  const [impliedConfirm, setImpliedConfirm] = useState<boolean>(false);

  /**
   * Start this sharing flow's run if it hasn't started one yet, and return its
   * starting leg id (#455).
   *
   * A sharing run is the indefinite propagation of a topic: the whole tree of
   * legs descending from this starting leg, across every degree of reshare, for as
   * long as it keeps spreading. A sharing flow — one trip through this wizard —
   * starts exactly one run and sends its first degree, so every leg the flow
   * goes on to send hangs off this starting leg. That makes texting N people one run
   * rather than N, and it holds for every topic type, not just news.
   *
   * The sender's own sharing-story contributions are frozen onto the starting
   * leg. `startingLegStoryFor` is what resolves them — this hook sends what it is
   * handed and does not know where an allowed detail or an allowed name comes
   * from.
   */
  const ensureSharingRun = useCallback(
    async (intent: MessageIntent): Promise<string | undefined> => {
      if (!topic) return undefined;
      const existing = readSharingRunId();
      if (existing) return existing;
      const run = await MePrFoService.initiateSharingRun(
        topic.id,
        startingLegStoryFor(intent)
      );
      setSharingRunId(run.leg.id);
      return run.leg.id;
    },
    [topic, startingLegStoryFor, readSharingRunId, setSharingRunId]
  );

  const openSMSLinkForLeg = useCallback(
    async (
      draft: MessageDraft,
      hasMoreToShare: boolean,
      existingInProgressId?: string,
      nextCursor?: number
    ) => {
      if (!topic) return;

      let legId = existingInProgressId;
      if (!legId) {
        const intent = draft.header.intent;
        const isReshare = draft.header.type === 'reshare';
        // The sender's own sharing-story contributions, as the composer
        // resolved them for this leg. This hook sends what it is handed: which
        // field holds an allowed detail, whether the message asks for one at all,
        // and where the allowed name comes from are all `legStoryFor`'s to know.
        const story = legStoryFor(draft);
        // Where this leg came from. A reshare names the leg it is passing
        // on and joins that run; every other send belongs to the run this
        // sharing flow started, so it hangs off that starting leg (#455).
        // handleComposeNext starts the run before queueing, so the ref is
        // normally already set — the call here covers a session restored from a
        // queue that predates the starting leg.
        const runPreviousLegId = isReshare
          ? previousLegId
          : await ensureSharingRun(intent);
        const result = await MePrFoService.initiateSharingLeg({
          ...sharingLegDetailsFor(draft, {
            topicId: topic.id,
            story,
            previousLegId: runPreviousLegId,
            updateSummary,
            aboutChangesSince,
          }),
          recipients: draft.header.recipients.map(c =>
            ContactLogic.toContactLike(c)
          ),
        });
        legId = result.leg.id;
        if (result.reshareInfo) {
          setLastReshareInfo(result.reshareInfo);
        }

        const enrichPayload = contactEnrichmentsForLeg(
          draft.header.recipients,
          result
        );
        if (enrichPayload.length > 0) {
          dispatch({ type: 'ENRICH_CONTACTS', payload: enrichPayload });
        }

        setInProgressLegId(legId);
        // Explicitly persist before handing off to SMS. Capacitor Preferences is
        // async; relying on the post-commit useEffect means Android may suspend
        // the webview before the write completes. The leg and cursor are passed
        // as overrides because this runs in the same tick as the state updates
        // above, so the hook has not seen them yet.
        await persistNow({
          inProgressLegId: legId,
          sendQueueCursor: nextCursor,
        });
      }

      const smsBody = finalizeBody(draft, legId);
      const smsLink = ContactLogic.createSMSLink(
        draft.header.recipients,
        smsBody,
        deviceInfo
      );
      window.dispatchEvent(
        new CustomEvent('share-wizard:sms-link', { detail: { smsLink } })
      );
      if (!('Cypress' in window)) {
        scheduleFCMReturnNotification(
          fcmToken,
          'Tap to return from sending',
          getReturnNotificationBody(draft.header.intent, hasMoreToShare),
          `${window.location.origin}/topics/${encodeURIComponent(topic.id)}/share/steps/share-confirm`
        ).catch(() => {});
        if (import.meta.env.MODE !== 'test') {
          // Let the SendingOverlay (shown by the event above) actually paint
          // before we hand off — otherwise Android backgrounds the WebView and
          // snapshots the underlying page for the app-switcher thumbnail before
          // React has committed the overlay frame. Two rAFs guarantees a paint.
          await new Promise<void>(resolve =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
          );
          window.location.href = smsLink;
          // Wait for the SMS app to take over before the caller navigates,
          // so the page-transition animation isn't visible while the OS
          // foregrounds Messages.
          await new Promise<void>(resolve => setTimeout(resolve, 2000));
        }
      }
    },
    [
      topic,
      aboutChangesSince,
      deviceInfo,
      dispatch,
      ensureSharingRun,
      fcmToken,
      finalizeBody,
      legStoryFor,
      // persistNow reads the live session through a ref, so the queue and
      // cursor are no longer captured here — it writes what is current at
      // handoff time rather than what this callback closed over.
      persistNow,
      previousLegId,
      setInProgressLegId,
      updateSummary,
    ]
  );

  const handleConfirmShare = useCallback(async () => {
    const currentShare = sendQueue[sendQueueCursor];
    if (!currentShare || !topic || !inProgressLegId) return;
    try {
      await MePrFoService.markLegAsSent(inProgressLegId);
      onLegSent(currentShare);

      const nextIndex = sendQueueCursor + 1;
      if (nextIndex >= sendQueue.length) {
        resetSession();
        dispatch({ type: 'CLEAR_RECIPIENTS' });
        nav.exitToOrigin();
      } else {
        setInProgressLegId(null);
        setSendQueueCursor(nextIndex);
        await openSMSLinkForLeg(
          sendQueue[nextIndex],
          nextIndex < sendQueue.length - 1,
          undefined,
          nextIndex
        );
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to confirm leg';
      presentToast({
        message: `Error: ${errorMsg}`,
        duration: 3000,
        color: 'danger',
      });
      // eslint-disable-next-line no-console
      console.error('Failed to confirm leg:', error);
    }
  }, [
    topic,
    sendQueue,
    nav,
    sendQueueCursor,
    dispatch,
    inProgressLegId,
    openSMSLinkForLeg,
    onLegSent,
    presentToast,
    resetSession,
    setInProgressLegId,
    setSendQueueCursor,
  ]);

  const handleOpenFirstShare = useCallback(async () => {
    const firstShare = sendQueue[0];
    if (!firstShare) return;
    const isLoneShare = sendQueue.length === 1;
    // For a lone share the sender lands on final-share-confirm once they return
    // from Messaging. Navigate there *before* the SMS handoff (not after
    // openSMSLinkForLeg resolves) so the SendingOverlay — shown by that handoff
    // and captured for the app-switcher thumbnail — covers final-share-confirm,
    // the page the sender comes back to. Navigating afterwards left the overlay
    // covering share-confirm while the forward hop ran only once the user was
    // already back, so "Waiting for you to finish sending" never showed for the
    // destination step. This mirrors the multi-leg flow, where the overlay
    // always covers the page the user returns to.
    if (isLoneShare) {
      nav.goTo(Destinations.sent, 'forward');
    }
    await openSMSLinkForLeg(firstShare, !isLoneShare);
  }, [sendQueue, openSMSLinkForLeg, nav]);

  const handleConfirmAndOpenNext = useCallback(async () => {
    const currentShare = sendQueue[sendQueueCursor];
    if (!currentShare || !topic || !inProgressLegId) return;
    try {
      await MePrFoService.markLegAsSent(inProgressLegId);
      // Apply the awareness cache here (not eagerly on implied confirm) so it
      // runs exactly once when the user proceeds, whether the leg was
      // confirmed manually or via a leg-link tap.
      onLegSent(currentShare);
      setImpliedConfirm(false);

      const nextIndex = sendQueueCursor + 1;
      setInProgressLegId(null);
      setSendQueueCursor(nextIndex);

      const nextShare = sendQueue[nextIndex];
      if (nextShare) {
        const isLastBatch = nextIndex === sendQueue.length - 1;
        // Mirror handleOpenFirstShare: when the next share is the final one, jump
        // to final-share-confirm before the handoff so the SendingOverlay covers
        // the page the sender returns to instead of share-confirm.
        if (isLastBatch) {
          nav.goTo(Destinations.sent, 'forward');
        }
        await openSMSLinkForLeg(nextShare, !isLastBatch, undefined, nextIndex);
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to confirm leg';
      presentToast({
        message: `Error: ${errorMsg}`,
        duration: 3000,
        color: 'danger',
      });
      // eslint-disable-next-line no-console
      console.error('Failed to confirm leg:', error);
    }
  }, [
    topic,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    onLegSent,
    openSMSLinkForLeg,
    nav,
    presentToast,
    setInProgressLegId,
    setSendQueueCursor,
  ]);

  const handleConfirmFinalShare = useCallback(async () => {
    const currentShare = sendQueue[sendQueueCursor];
    if (!currentShare || !topic || !inProgressLegId) return;
    try {
      await MePrFoService.markLegAsSent(inProgressLegId);
      onLegSent(currentShare);

      const totalCount = sendQueue.reduce(
        (n, d) => n + d.header.recipients.length,
        0
      );
      resetSession();
      // Emptying the queue while a confirmed count stands is what
      // FinalShareConfirmStep reads as "done", so it re-renders inline as the
      // completion screen.
      setSendQueue([]);
      setSendQueueCursor(0);
      setTotalConfirmedCount(totalCount);
      dispatch({ type: 'CLEAR_RECIPIENTS' });
      // No navigation — FinalShareConfirmStep re-renders inline as the
      // completion screen when sendQueue is empty and totalConfirmedCount > 0.
      // Avoids an Ionic page transition that gets stuck (entering page
      // mis-tagged with ion-page-hidden) when the prior share-confirm →
      // final-share-confirm animation was interrupted by the SMS handoff in
      // the multi-leg flow.
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to confirm leg';
      presentToast({
        message: `Error: ${errorMsg}`,
        duration: 3000,
        color: 'danger',
      });
      // eslint-disable-next-line no-console
      console.error('Failed to confirm previous leg:', error);
    }
  }, [
    topic,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    onLegSent,
    dispatch,
    presentToast,
    resetSession,
    setSendQueue,
    setSendQueueCursor,
  ]);

  const handleRetryCurrentShare = useCallback(() => {
    openSMSLinkForLeg(
      sendQueue[sendQueueCursor],
      sendQueueCursor < sendQueue.length - 1,
      inProgressLegId ?? undefined
    );
  }, [sendQueue, sendQueueCursor, inProgressLegId, openSMSLinkForLeg]);

  const handleCancelSending = useCallback(() => {
    // Clear localStorage before exiting so returning to the wizard starts fresh.
    // Don't reset inProgressLegId/sendQueue — that would trigger the step-snap
    // effect (which guards share-confirm based on inProgressLegId) and cause a
    // competing inner-router navigation that fights the exit. The stale state
    // is garbage-collected when the wizard provider unmounts after the transition.
    clearStoredSession();
    nav.exit();
  }, [clearStoredSession, nav]);

  const handleInterruptSending = useCallback(() => {
    clearStoredSession();
    // When returning via notification (fresh tab), in-memory recipients are empty
    // even though sendQueue was restored from persisted state. Rehydrate from sendQueue
    // so RecipientsStep is populated after navigating back.
    if (recipients.length === 0 && sendQueue.length > 0) {
      const allContacts = sendQueue.flatMap(b => b.header.recipients);
      const unique = Array.from(
        new Map(allContacts.map(c => [c.localId, c])).values()
      );
      dispatch({ type: 'SET_RECIPIENTS', payload: unique });
      const restored = new Set<string>();
      for (let i = 0; i < sendQueueCursor; i++) {
        sendQueue[i]?.header.recipients.forEach(c => restored.add(c.localId));
      }
      if (restored.size > 0) setConfirmedContactIds(restored);
    }
    setInProgressLegId(null);
    setSendQueue([]);
    setSendQueueCursor(0);
    // confirmedContactIds intentionally preserved so RecipientsStep can show decorations
    nav.goTo(Destinations.recipients, 'back');
  }, [
    clearStoredSession,
    sendQueue,
    sendQueueCursor,
    dispatch,
    nav,
    recipients,
    setConfirmedContactIds,
    setInProgressLegId,
    setSendQueue,
    setSendQueueCursor,
  ]);

  return {
    // starting and sending
    ensureSharingRun,
    handleOpenFirstShare,
    handleRetryCurrentShare,

    // confirming
    handleConfirmShare,
    handleConfirmAndOpenNext,
    handleConfirmFinalShare,
    impliedConfirm,
    setImpliedConfirm,
    totalConfirmedCount,
    lastReshareInfo,

    // getting out
    handleCancelSending,
    handleInterruptSending,
  };
}
